import { SelectOption } from '@shared/components/form/select-form-field';
import { ErrorLevel } from 'react-spreadsheet-import/types/types';
import { object, z } from 'zod';

export const UK_DATE_FORMAT = 'dd/MM/yyyy';
export const ERROR_MESSAGES = {
  UNIT_REQUIRED_WITH_WEIGHT: 'Unit is required when total weight is provided',
  INVALID_TOTAL_WEIGHT: 'Total weight must be a valid number',
  NEGATIVE_TOTAL_WEIGHT: 'Total weight must be a number greater than zero',
  INVALID_QUANTITY: 'Quantity must be a valid number',
  NEGATIVE_QUANTITY: 'Quantity must be a number greater than zero',
  DATE_REQUIRED: 'Date is required',
  INVALID_DATE_FORMAT: 'Invalid date format, must be dd/mm/yyyy',
  DATE_OUT_OF_RANGE: 'Date must be within the your reporting periods',
} as const;

/** The activity types a single import can contain. The user picks these on the
 *  activity-types step; the selected array is sent to the backend, which
 *  decides per the matrix whether to force a single type, treat unselected
 *  detections as "other", or classify per-row. */
export type ActivityType = 'food' | 'packaging' | 'other';

export const importUploadSchema = object({
  siteUuid: z.string().min(1).optional(),
  siteMethod: z.string().min(1),
  periodMethod: z.string().min(1),
  timePeriodYear: z.string().min(1).optional(),
  timePeriodMonthIndex: z.coerce.number().min(1).max(12).optional(),
  tags: z.array(z.string()).optional(),
  activityTypes: z.array(z.enum(['food', 'packaging', 'other'])).optional(),
});

export const siteMethodOptions = [
  {
    label: 'Automatically from a column in my data',
    value: 'auto',
  },
  {
    label: 'I will select a single site for this import',
    value: 'single',
  },
  {
    label: 'Do not assign a site for this data',
    value: 'none',
  },
];

export const periodMethodOptions: SelectOption[] = [
  {
    label: 'Automatically from a column in my data',
    value: 'auto',
  },
  {
    label: 'I will select a time period for this import',
    value: 'single',
  },
];

export function addValidationError<TData>(
  addError: (
    field: keyof TData,
    error: { level: ErrorLevel; message: string }
  ) => void,
  field: keyof TData,
  level: ErrorLevel,
  message: string
): void {
  addError(field, { level, message });
}

/**
 * Parses a numeric string to a float, removing comma thousand separators.
 * Handles numbers like "1,234.56" or "2,500" correctly.
 *
 * @param value - The string value to parse (may contain commas)
 * @returns The parsed float value, or NaN if invalid
 *
 * @example
 * parseNumberWithCommas("2,500") // 2500
 * parseNumberWithCommas("1,234.56") // 1234.56
 * parseNumberWithCommas("-1,234") // -1234
 */
export function parseNumberWithCommas(value: string | undefined): number {
  if (!value || typeof value !== 'string') {
    return NaN;
  }
  return parseFloat(value.replace(/,/g, ''));
}
